import React, { useState, useEffect } from 'react';
import './TaskManager.css';
import api from './api';
import useAuth from "../hooks/useAuth";
import { canDo } from '../utils/canDo';
import { FaEdit, FaTrash, FaCopy, FaStar } from 'react-icons/fa';
import CustomDataTable from './CustomDataTable';

export default function TaskManager({ userFilter }) {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const { auth } = useAuth();
  const [editingTask, setEditingTask] = useState(null);
const [showOnlyNeedsAction, setShowOnlyNeedsAction] = useState(false);
const [statusFilter, setStatusFilter] = useState('delayed');
const [urgencyFilter, setUrgencyFilter] = useState('');
const [assigneeFilter, setAssigneeFilter] = useState('');
const [dateRangeFilter, setDateRangeFilter] = useState(() => {
  const d = new Date(); d.setMonth(d.getMonth() - 2);
  return { from: d.toISOString().split('T')[0], to: '' };
});
const [newTask, setNewTask] = useState({
  title: '',
  assignee: '',
  frequency: 'OneTime',
  frequencyDays: [], // ✅ Add this
  blueStars: 0,
  redStars: 0,
  blueStarValue: 10,
  redStarValue: -10,
  urgentLevel: 'Important',
  notes: '',
  estimatedEndDate: '',
  totalWorth: 1 // ✅ NEW
});


  // Check if logged-in user is superadmin
  const isSuperadmin = canDo(auth, 'checklist.edit');

  useEffect(() => {
    fetchTasks();
    if (isSuperadmin) fetchUsers();
  }, [userFilter, statusFilter]); // refetch whenever parent filter or status filter changes

  
// utils/calculateTaskStars.js
function calculateTaskStars(task, today = new Date()) {
  const estimated = task.estimatedEndDate ? new Date(task.estimatedEndDate) : null;
  const final = task.finalEndDate ? new Date(task.finalEndDate) : null;
  const status = task.status;
  const taskCreated = task.createdAt;

  let redStars = 0;
  let blueStars = 0;
  let needsUserAction = false;
  let updatedStatus = status;

  if (!estimated) {
    needsUserAction = true;
    return { redStars, blueStars, needsUserAction, updatedStatus };
  }

  
  // Case 2: Pending/Delayed, estimated passed, no finalEndDate
  if ((status === "new") && today > taskCreated) {
    updatedStatus = "pending";
  }
  
  // Case 1: Completed before or on estimatedEndDate
  if (status === "completed" && today <= estimated) {
    blueStars = 1;
    redStars = 0;
  }

  // Case 2: Pending/Delayed, estimated passed, no finalEndDate
  else if ((status === "pending" || status === "delayed") && today > estimated && !final) {
    redStars = 1;
    blueStars = 0;
    needsUserAction = true;
    updatedStatus = "delayed";
  }

  // Case 3: Completed after estimatedEndDate
  else if (status === "completed" && final && final > estimated) {
    redStars = task.merit?.redStars || 1;
    blueStars = 0;
  }

  // Case 4: Still delayed even after finalEndDate
  else if (status === "delayed" && final && final > estimated) {
    const overdueDays = Math.ceil((final - estimated) / (1000 * 60 * 60 * 24));
    redStars = Math.min(overdueDays, 5);
    blueStars = 0;
  }

  return { redStars, blueStars, needsUserAction, updatedStatus };
}

function getOverdueDays(task) {
  if (!task.estimatedEndDate || task.status === 'completed') return null;

  const today = new Date();
  const estimated = new Date(task.estimatedEndDate);
  const final = task.finalEndDate ? new Date(task.finalEndDate) : null;

  if (final && final <= estimated) return null;

  const baseDate = final || today;
  const diffMs = baseDate - estimated;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : null;
}

async function fetchTasks() {
  try {
    // userFilter = 'all' → fetch all tasks; otherwise fetch for the given username
    const ur = (!userFilter || userFilter === 'all') ? '' : '/' + userFilter;
    const qp = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';

    const res = await api.get(process.env.REACT_APP_URL + '/tasks' + ur + qp);
    const tasks = res.data;
    const tasksToUpdate = [];

    const now = new Date();

    tasks.forEach(task => {
      const { redStars, blueStars, needsUserAction, updatedStatus } = calculateTaskStars(task, now);
      console.log("stars = ", redStars, blueStars);
      const currentRedStars = task.merit?.redStars || 0;
      const currentBlueStars = task.merit?.blueStars || 0;

      task.needsUserAction = needsUserAction; // Add this for UI styling

      if ((redStars !== currentRedStars || blueStars !== currentBlueStars) && isSuperadmin) {
        tasksToUpdate.push({
          _id: task._id,
          merit: { redStars, blueStars },
          status: updatedStatus
        });
      }
    });

    if (tasksToUpdate.length > 0) {
      await api.post(`${process.env.REACT_APP_URL}/tasks/bulk-update`, {
        tasks: tasksToUpdate,
        user: { roles: auth.roles, username: auth.username }
      });

      const updatedRes = await api.get(process.env.REACT_APP_URL + '/tasks' + ((!userFilter || userFilter === 'all') ? '' : '/' + userFilter) + qp);
      setTasks(updatedRes.data);
    } else {
      setTasks(tasks);
    }

  } catch (err) {
    console.error("Failed to fetch or update tasks", err);
  }
}

function handleUpdateTask(e) {
  e.preventDefault();

  const updatedTask = { 
    ...editingTask, 
    totalWorth: editingTask.totalWorth || 1
  };

  const { redStars, blueStars, updatedStatus } = calculateTaskStars(editingTask, new Date());

  updatedTask.merit = {
    blueStars,
    redStars
  };

  updatedTask.status = updatedStatus;

  if (redStars === 1 && (updatedStatus === "delayed") && !editingTask.finalEndDate) {
    alert("Final End Date is required as task is overdue.");
  }

  api.post(`${process.env.REACT_APP_URL}/tasks/${editingTask._id}`, {
    task: updatedTask,
    user: { roles: auth.roles, username: auth.username },
  })
    .then(res => {
      setEditingTask(null);
      fetchTasks();
    })
    .catch(err => console.error("Failed to update task", err));
}

  // Fetch users (therapists)
  function fetchUsers() {
  
    api.get(process.env.REACT_APP_URL + '/users/therapist')
      .then(res => setUsers(res.data))
      .catch(err => console.error("Failed to fetch users", err));
  }

  // Reset new task form state
  function resetNewTask() {
    setNewTask({
      title: '',
      assignee: '',
      frequency: 'Daily',
      frequencyDays: [],
      blueStars: 0,
      redStars: 0,
      blueStarValue: 10,
      redStarValue: -10,
      urgentLevel: 'Important',
      notes: '',
      estimatedEndDate: ''
    });
  }

  // Create new task handler
  function handleCreateTask(e) {
    e.preventDefault();
    const payload = {
      ...newTask,
      createdBy: auth?.username || '',
      totalWorth: Number(newTask.totalWorth) || 1, // ✅ include
      merit: {
        blueStars: 0,
        redStars: 0,
        blueStarValue: newTask.blueStarValue,
        redStarValue: newTask.redStarValue
      }
    };

    api.post(process.env.REACT_APP_URL + '/tasks', {task: payload, user: {roles: auth.roles, username: auth.username}})
      .then(() => {
        setShowModal(false);
        resetNewTask();
        fetchTasks();
      })
      .catch(err => console.error("Failed to create task", err));
  }

  // Duplicate existing task into new task form
  function handleDuplicateTask(task) {
    setNewTask({
      title: task.title || '',
      assignee: task.assignee || '',
      frequency: task.frequency || 'Daily',
      blueStars: task.merit?.blueStars || 0,
      redStars: task.merit?.redStars || 0,
      blueStarValue: task.merit?.blueStarValue || 10,
      redStarValue: task.merit?.redStarValue || -10,
      urgentLevel: task.urgentLevel || 'Important',
      notes: task.notes || '',
      estimatedEndDate: '',
    });
    setShowModal(true);
  }

function handleStatusUpdate(taskId, field, value) {
  const task = tasks.find(t => t._id === taskId);
  if (!task) return;

  const today = new Date();
  const estimated = task.estimatedEndDate ? new Date(task.estimatedEndDate) : null;
  const final = field === 'finalEndDate' ? new Date(value) : (task.finalEndDate ? new Date(task.finalEndDate) : null);

  // Start with current merit values, don't reset to zero blindly
  const updated = {
    [field]: value,
    merit: { ...task.merit }, // keep existing stars unless logic changes them
    status: task.status // keep current status unless logic changes it
  };

  if (field === 'finalEndDate' || field === 'estimatedEndDate') {
    if (estimated) {
      if (final) {
        const diffDays = Math.ceil((final - estimated) / (1000 * 60 * 60 * 24));
        if (diffDays > 0) {
          updated.merit.redStars = Math.min(diffDays, 5);
          updated.merit.blueStars = 0;
          updated.status = 'delayed';
        } 
        /*else 
        {
          updated.merit.blueStars = 1; // Assign 1 blue star if completed on time or early
          updated.merit.redStars = 0;
          updated.status = 'completed';
        }*/
      } else {
        const overdueDays = Math.ceil((today - estimated) / (1000 * 60 * 60 * 24));
        if (overdueDays > 0) {
          updated.merit.redStars = Math.min(overdueDays, 5);
          updated.merit.blueStars = 0;
          updated.status = 'delayed';
        }
      }
    } 
    else 
    {
      updated.status = value ? 'completed' : 'pending';
    }
  }

  api.post(`${process.env.REACT_APP_URL}/tasks/${taskId}`, { task: updated, user: { roles: auth.roles, username: auth.username } })
    .then(res => {
      setTasks(tasks.map(t => t._id === taskId ? res.data : t));
      if (editingTask && editingTask._id === taskId) {
        setEditingTask(res.data);
      }
    })
    .catch(err => console.error("Failed to update status", err));
}

  // Delete task with confirmation
  function handleDeleteTask(taskId) {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    //api.delete(process.env.REACT_APP_URL + '/tasks/' + taskId)
    
    api.post(`${process.env.REACT_APP_URL}/tasks/delete/${taskId}`, {
      user: { roles: auth.roles, username: auth.username },
      })
      .then(() => setTasks(tasks.filter(t => t._id !== taskId)))
      .catch(err => alert(err));
  }

  // Helper to get user display name from username or ID
  function getUserName(identifier) {
    if (!identifier) return '';
    const user = users.find(u => u.username === identifier || u._id === identifier);
    return user?.username || identifier;
  }

function handleStarAdjustmentOld(taskId, type) {
  const task = tasks.find(t => t._id === taskId);
  if (!task || !task.merit) return;

  const updatedMerit = { ...task.merit };

  if (type === 'blue') {
    if (updatedMerit.blueStars < 5) {
      updatedMerit.blueStars += 1;
    }
  } else if (type === 'red') {
    if (updatedMerit.redStars > 0) {
      updatedMerit.redStars -= 1;
    }
  }

  const updatedTask = {
    ...task, // include all task properties
    merit: updatedMerit,
    status: task.status, // ensure status is preserved
  };

  api.post(`${process.env.REACT_APP_URL}/tasks/${taskId}`, {
    task: updatedTask,
    user: { roles: auth.roles, username: auth.username },
  })
    .then(res => {
      setTasks(tasks.map(t => t._id === taskId ? res.data : t));
    })
    .catch(err => console.error("Failed to adjust stars", err));
}

function handleStarAdjustment(taskId, type) {

  const task = tasks.find(t => t._id === taskId);
  if (!task ) return;

   const newWorth = prompt(
    `Enter new total worth (current: ${task.totalWorth || 0}):`,
    task.totalWorth || 1
  );

  // Validate user input
  if (newWorth === null) return; // User cancelled
  const worthValue = parseInt(newWorth, 10);

  if (isNaN(worthValue) || worthValue < 0) {
    alert("⚠️ Please enter a valid number greater than 0.");
    return;
  }

  const updatedTask = { ...task, totalWorth: worthValue };

  api.post(`${process.env.REACT_APP_URL}/tasks/${taskId}`, {
    task: updatedTask,
    user: { roles: auth.roles, username: auth.username },
  })
    .then(res => {
      setTasks(tasks.map(t => t._id === taskId ? res.data : t));
    })
    .catch(err => console.error("Failed to adjust stars", err));
}


  // Compute filtered tasks
const filteredTasks = tasks.filter(task => {
  const matchesStatus = statusFilter ? task.status === statusFilter : true;
  const matchesUrgency = urgencyFilter ? task.urgentLevel === urgencyFilter : true;
  const matchesAssignee = assigneeFilter ? task.assignee === assigneeFilter : true;

  const taskDate = task.estimatedEndDate ? new Date(task.estimatedEndDate) : null;
  const fromDate = dateRangeFilter.from ? new Date(dateRangeFilter.from) : null;
  const toDate = dateRangeFilter.to ? new Date(dateRangeFilter.to) : null;

  const matchesDateRange =
    taskDate &&
    (!fromDate || taskDate >= fromDate) &&
    (!toDate || taskDate <= toDate);

  const matchesDateFilter = !fromDate && !toDate ? true : matchesDateRange;

  const matchesNeedsAction = showOnlyNeedsAction ? task.needsUserAction : true;

  return (
    matchesStatus &&
    matchesUrgency &&
    matchesAssignee &&
    matchesDateFilter &&
    matchesNeedsAction
  );
});

  return (
    <div className="task-manager">
      <h2>One Time Task Manager</h2>
    

{/* Create New Task Modal */}
{!showModal && !editingTask && (

  <button onClick={() => setShowModal(true)}>Create New Task</button>
)}
{showModal && !editingTask && (
  <div className="tm-overlay" onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
    <div className="tm-modal">
      <div className="tm-modal-header">
        <h3>Create New Task</h3>
        <button className="tm-modal-close" onClick={() => setShowModal(false)}>&times;</button>
      </div>
      <form onSubmit={handleCreateTask}>
        <div className="tm-modal-body">
          <div className="tm-field">
            <label>Title</label>
            <input type="text" required value={newTask.title}
              onChange={e => setNewTask({ ...newTask, title: e.target.value })}
              placeholder="What needs to be done?" />
          </div>

          <div className="tm-row">
            <div className="tm-field">
              <label>Assignee</label>
              <select required value={newTask.assignee}
                onChange={e => setNewTask({ ...newTask, assignee: e.target.value })}>
                <option value="">Select staff</option>
                {users.map(u => (
                  <option key={u._id} value={u.username}>{u.username}</option>
                ))}
              </select>
            </div>
            <div className="tm-field">
              <label>Urgency Level</label>
              <select value={newTask.urgentLevel}
                onChange={e => setNewTask({ ...newTask, urgentLevel: e.target.value })}>
                <option>Urgent</option>
                <option>Important</option>
                <option>Scheduled</option>
                <option>Some Day</option>
              </select>
            </div>
          </div>

          <div className="tm-row">
            <div className="tm-field">
              <label>Due Date</label>
              <input type="date"
                value={newTask.estimatedEndDate ? newTask.estimatedEndDate.split('T')[0] : ''}
                onChange={e => setNewTask({ ...newTask, estimatedEndDate: e.target.value })} />
            </div>
            <div className="tm-field">
              <label>Total Worth</label>
              <input type="number" min="1" required value={newTask.totalWorth}
                onChange={e => setNewTask({ ...newTask, totalWorth: Math.max(1, Number(e.target.value)) })} />
              <div className="tm-stars-preview">
                {[...Array(Number(newTask.totalWorth) || 0)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
            </div>
          </div>

          <div className="tm-field">
            <label>Notes</label>
            <textarea rows={3} value={newTask.notes}
              onChange={e => setNewTask({ ...newTask, notes: e.target.value })}
              placeholder="Additional details or instructions..." />
          </div>

          {newTask.frequency === 'Other' && (
            <div className="tm-field">
              <label>Select Days of Week</label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <label key={day} style={{ userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    <input type="checkbox"
                      checked={newTask.frequencyDays?.includes(day) || false}
                      onChange={e => {
                        const newDays = newTask.frequencyDays ? [...newTask.frequencyDays] : [];
                        if (e.target.checked) { if (!newDays.includes(day)) newDays.push(day); }
                        else { const idx = newDays.indexOf(day); if (idx > -1) newDays.splice(idx, 1); }
                        setNewTask({ ...newTask, frequencyDays: newDays });
                      }} />
                    {day}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="tm-modal-footer">
          <button type="button" className="tm-btn tm-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
          <button type="submit" className="tm-btn tm-btn-primary">Create Task</button>
        </div>
      </form>
    </div>
  </div>
)}

{/* Edit Task Modal */}
{editingTask && (
  <div className="tm-overlay" onClick={e => { if (e.target === e.currentTarget) setEditingTask(null); }}>
    <div className="tm-modal">
      <div className="tm-modal-header">
        <h3>Edit Task</h3>
        <button className="tm-modal-close" onClick={() => setEditingTask(null)}>&times;</button>
      </div>
      <form onSubmit={handleUpdateTask}>
        <div className="tm-modal-body">
          {/* Title */}
          <div className="tm-field">
            <label>Title</label>
            {isSuperadmin ? (
              <input type="text" required value={editingTask.title}
                onChange={e => setEditingTask({ ...editingTask, title: e.target.value })} />
            ) : (
              <div className="tm-readonly">{editingTask.title}</div>
            )}
          </div>

          {/* Assignee + Urgency */}
          <div className="tm-row">
            <div className="tm-field">
              <label>Assignee</label>
              {isSuperadmin ? (
                <select required value={editingTask.assignee}
                  onChange={e => setEditingTask({ ...editingTask, assignee: e.target.value })}>
                  <option value="">Select</option>
                  {users.map(u => (
                    <option key={u._id} value={u.username}>{u.username}</option>
                  ))}
                </select>
              ) : (
                <div className="tm-readonly">{editingTask.assignee}</div>
              )}
            </div>
            <div className="tm-field">
              <label>Urgency Level</label>
              {isSuperadmin ? (
                <select value={editingTask.urgentLevel}
                  onChange={e => setEditingTask({ ...editingTask, urgentLevel: e.target.value })}>
                  <option>Urgent</option>
                  <option>Important</option>
                  <option>Scheduled</option>
                  <option>Some Day</option>
                </select>
              ) : (
                <div className="tm-readonly">{editingTask.urgentLevel}</div>
              )}
            </div>
          </div>

          {/* Due Date + Worth + Status */}
          <div className="tm-row">
            {isSuperadmin && (
              <div className="tm-field">
                <label>Due Date</label>
                <input type="date"
                  value={editingTask.estimatedEndDate ? editingTask.estimatedEndDate.split('T')[0] : ''}
                  onChange={e => setEditingTask({ ...editingTask, estimatedEndDate: e.target.value })} />
              </div>
            )}
            <div className="tm-field">
              <label>Total Worth</label>
              {isSuperadmin ? (
                <>
                  <input type="number" min="1" value={editingTask.totalWorth || 1}
                    onChange={e => setEditingTask({ ...editingTask, totalWorth: Math.max(1, Number(e.target.value)) })} />
                  <div className="tm-stars-preview">
                    {[...Array(Number(editingTask.totalWorth) || 0)].map((_, i) => (
                      <FaStar key={i} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="tm-stars-preview">
                  {[...Array(Number(editingTask.totalWorth) || 0)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
              )}
            </div>
            <div className="tm-field">
              <label>Status</label>
              {(isSuperadmin || auth?.username === editingTask.assignee) ? (
                <select value={editingTask.status || ''}
                  onChange={e => setEditingTask({ ...editingTask, status: e.target.value })}>
                  <option value="new">New</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="delayed">Delayed</option>
                </select>
              ) : (
                <div className="tm-readonly">{editingTask.status}</div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="tm-field">
            <label>Notes</label>
            {(isSuperadmin || auth?.username === editingTask.assignee) ? (
              <textarea rows={3} value={editingTask.notes}
                onChange={e => setEditingTask({ ...editingTask, notes: e.target.value })}
                placeholder="Additional details or instructions..." />
            ) : (
              <div className="tm-readonly">{editingTask.notes || 'No notes'}</div>
            )}
          </div>
        </div>

        <div className="tm-modal-footer">
          <button type="button" className="tm-btn tm-btn-cancel" onClick={() => setEditingTask(null)}>Cancel</button>
          <button type="submit" className="tm-btn tm-btn-primary">Save Changes</button>
        </div>
      </form>
    </div>
  </div>
)}


{!showModal && !editingTask && (
  <div>
     {/* ✅ Filter Toggle */}
     {/*
     <div style={{ margin: '10px' }}>
          <label> Show only tasks that need user action    </label>
            <input
              type="checkbox"
              checked={showOnlyNeedsAction}
              onChange={() => setShowOnlyNeedsAction(!showOnlyNeedsAction)}
              style={{  margin: -149,  height: 31  }}
            />  
     </div>
     */}
     <div className="filters"
       style={{
        margin: '8px 0',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '5px 8px',
        fontSize: '12px',
          }}
        >
        {/* Status Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 82 }}>
            <option value="">All</option>
            <option value="new">New</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="delayed">Delayed</option>
          </select>
        </div>

        {/* Urgency Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>Urgency</label>
          <select value={urgencyFilter} onChange={(e) => setUrgencyFilter(e.target.value)}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 90 }}>
            <option value="">All</option>
            <option value="Urgent">Urgent</option>
            <option value="Important">Important</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Some Day">Some Day</option>
          </select>
        </div>

        {/* Assignee Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>Assignee</label>
          <select value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 95 }}>
            <option value="">All</option>
            {users.map((u) => (
              <option key={u._id} value={u.username}>{u.username}</option>
            ))}
          </select>
        </div>

        {/* Date From */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>From</label>
          <input type="date" value={dateRangeFilter.from}
            onChange={(e) => setDateRangeFilter((prev) => ({ ...prev, from: e.target.value }))}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 115 }} />
        </div>

        {/* Date To */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>To</label>
          <input type="date" value={dateRangeFilter.to}
            onChange={(e) => setDateRangeFilter((prev) => ({ ...prev, to: e.target.value }))}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 115 }} />
        </div>

        {/* All Data */}
        <button
          onClick={() => setDateRangeFilter({ from: '', to: '' })}
          style={{ border: '1px solid #3b82f6', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 600, height: 26, cursor: 'pointer', background: (!dateRangeFilter.from && !dateRangeFilter.to) ? '#3b82f6' : '#eff6ff', color: (!dateRangeFilter.from && !dateRangeFilter.to) ? '#fff' : '#2563eb' }}>
          All Data
        </button>

        {/* Reset Button */}
        <button
          onClick={() => {
            setStatusFilter('delayed');
            setUrgencyFilter('');
            setAssigneeFilter('');
            const d = new Date(); d.setMonth(d.getMonth() - 2);
            setDateRangeFilter({ from: d.toISOString().split('T')[0], to: '' });
          }}
          style={{ border: '1px solid #ccc', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 600, height: 26, cursor: 'pointer', background: '#f1f5f9', color: '#475569' }}>
          Reset
        </button>
      </div>

        {/* ── Task Cards ── */}
        <div className="task-grid">
          {filteredTasks.map(task => (
            <div
              key={task._id}
              className={`task-card ${task.status.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {/* Attention banner */}
              {task.needsUserAction && (
                <div className="attention-banner">
                  <span>⚠️ Needs Your Action</span>
                  {!task.estimatedEndDate && (
                    <span className="overdue-info">(No due date set)</span>
                  )}
                  {task.estimatedEndDate && getOverdueDays(task) && (
                    <span className="overdue-info">
                      ({getOverdueDays(task)} day{getOverdueDays(task) > 1 ? 's' : ''} overdue)
                    </span>
                  )}
                </div>
              )}

              {/* Header: Title + Status + Stars */}
              <div className="task-card-header">
                <h3 className="task-title">{task.title}</h3>
                <div className="task-meta-right">
                  <div className="task-stars-row">
                    {[...Array(Number(task.totalWorth) || 0)].map((_, i) => (
                      <FaStar key={i} />
                    ))}
                  </div>
                  <span className={`task-status-badge ${task.status.toLowerCase().replace(/\s+/g, '-')}`}>
                    {task.status}
                  </span>
                </div>
              </div>

              {/* Body: Metadata + Notes */}
              <div className="task-card-body">
                <div className="task-meta-row">
                  <span className="task-meta-item">
                    <span className="meta-icon">👤</span>
                    <strong>{getUserName(task.assignee) || '—'}</strong>
                  </span>
                  <span className={`task-urgency-badge ${task.urgentLevel?.replace(/\s+/g, '-')}`}>
                    {task.urgentLevel}
                  </span>
                  {task.estimatedEndDate && (
                    <span className="task-meta-item">
                      <span className="meta-icon">📅</span>
                      Due: {task.estimatedEndDate.split('T')[0].split('-').reverse().join('-')}
                    </span>
                  )}
                  {task.finalEndDate && (
                    <span className="task-meta-item">
                      <span className="meta-icon">✅</span>
                      Done: {task.finalEndDate.split('T')[0].split('-').reverse().join('-')}
                    </span>
                  )}
                  {!task.finalEndDate && getOverdueDays(task) && (
                    <span className="task-overdue-chip">
                      {getOverdueDays(task)}d overdue
                    </span>
                  )}
                </div>

                {task.notes && (
                  <div className="task-notes-section">
                    <span className="notes-label">Notes</span>
                    {task.notes}
                  </div>
                )}
              </div>

              {/* Footer: Actions + Merit */}
              <div className="task-card-footer">
                <div className="task-card-actions">
                  <FaEdit title="Edit" onClick={() => setEditingTask(task)} className="icon edit" />
                  <FaTrash title="Delete" onClick={() => handleDeleteTask(task._id)} className="icon delete" />
                  <FaCopy title="Duplicate" onClick={() => handleDuplicateTask(task)} className="icon duplicate" />
                  {isSuperadmin && (
                    <FaStar
                      title="Adjust Worth"
                      onClick={() => handleStarAdjustment(task._id, 'blue')}
                      className="icon star"
                    />
                  )}
                </div>
                <div className="task-merit-stars">
                  {(task.merit?.greenStars || 0) > 0 && (
                    <span className="green-stars">
                      +{task.merit.greenStars} <FaStar />
                    </span>
                  )}
                  {(task.merit?.redStars || 0) > 0 && (
                    <span className="red-stars">
                      -{task.merit.redStars} <FaStar />
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      
      </div>
    )}
  </div>
  );
}

